import { HttpClient, PaginatedResponse, robotoHeaders } from "../../http";
import { type CustomFieldChangeset } from "../customFields";
import { MetadataChangeset } from "../metadataChangeset";
import { Token } from "../tokens";

import { type Device } from "./Device";

interface Options {
  abortSignal: AbortSignal;
  resourceOwnerId: string;
  searchParams: URLSearchParams;
  pageToken?: string;
}

export interface CreateDeviceRequest {
  /** Caller-chosen identifier, unique within the owning org. */
  device_id: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
  /** Initial values for Ready custom fields on this device. */
  custom_fields?: Record<string, unknown>;
  /** The `dt_` ID of the device's type; refused (400) while `release-asset-store` is off for the org. */
  device_type_id?: string;
}

export interface UpdateDeviceRequest {
  metadata_changeset?: MetadataChangeset;
  /** Set/clear Ready custom-field values; absent = no change. */
  custom_fields_changeset?: CustomFieldChangeset;
  /**
   * A `dt_` ID sets the device's type, `null` clears it, and leaving the field
   * out keeps the current type. A `dt_` ID is refused (400) while
   * `release-asset-store` is off for the org; `null` is not, so a type set
   * while the flag was on can still be cleared after it is turned off.
   */
  device_type_id?: string | null;
}

/**
 * A device id for interpolation into a URL path segment.
 *
 * Device ids are caller-chosen at registration and stored as a bare string, so
 * one may contain `/`, `#`, `%`, or a space. Every `/id/<device_id>` route on
 * the service percent-decodes what it receives (and the Python SDK encodes
 * with `quote(safe="")` to match), so an unencoded id here does not merely
 * look wrong — a slash splits the path segment and the request lands on a
 * different route, taking any suffix like `/tokens` with it.
 */
function encodeDeviceId(deviceId: string): string {
  return encodeURIComponent(deviceId);
}

export class DeviceService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  /**
   * Registers a new device under `options.orgId`.
   *
   * The route takes the owning org from the caller-org header rather than the
   * request body, so the org is sent as a header here and `org_id` is left off
   * the payload.
   */
  public async createDevice(
    request: CreateDeviceRequest,
    options: { orgId: string; abortSignal?: AbortSignal },
  ): Promise<Device> {
    const requestUrl = this.#httpClient.constructUrl("v1/devices/create");

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({
        orgId: options.orgId,
        resourceOwnerId: options.orgId,
      }),
      body: JSON.stringify(request),
      signal: options.abortSignal,
    });

    return await response.json<Device>();
  }

  public async getDevicesByOrg(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<Device>> {
    const searchParams = new URLSearchParams();

    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/devices/org/${orgId}`,
      searchParams,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<Device>>();
  }

  /**
   * Fetches all devices for an organization, handling pagination internally.
   * Returns a flat array of all Device objects.
   */
  public async getAllDevicesByOrg(
    orgId: string,
    options?: Partial<Pick<Options, "abortSignal">>,
  ): Promise<Device[]> {
    const allDevices: Device[] = [];
    let pageToken: string | undefined = undefined;

    while (true) {
      const response = await this.getDevicesByOrg(orgId, {
        abortSignal: options?.abortSignal,
        pageToken,
      });

      allDevices.push(...response.items);

      if (response.next_token) {
        pageToken = response.next_token;
      } else {
        break;
      }
    }

    return allDevices;
  }

  public async getDevice(
    deviceId: string,
    options?: Partial<Options>,
  ): Promise<Device> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/devices/id/${encodeDeviceId(deviceId)}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return await response.json<Device>();
  }

  public async getTokensForDevice(
    deviceId: string,
    options?: Partial<Options>,
  ): Promise<Token[]> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/devices/id/${encodeDeviceId(deviceId)}/tokens`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return await response.json<Token[]>();
  }

  /**
   * Preview the devices a set of files implies — the union of the `device_id`
   * recorded on each file and on its parent dataset — without creating a
   * session. The backend applies the same inference when files are attached
   * to a session, so the preview matches what a created session would get.
   * Returns the distinct device ids, unordered.
   *
   * Scoped to the org named by `resourceOwnerId`, which is required (throws
   * when absent, like {@link updateDevice}); files outside that org
   * contribute nothing.
   */
  public async inferDevices(
    fileIds: string[],
    options?: Partial<Pick<Options, "abortSignal" | "resourceOwnerId">>,
  ): Promise<string[]> {
    if (!options?.resourceOwnerId) {
      throw Error("inferDevices requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl("v1/devices/infer");
    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      body: JSON.stringify({ file_ids: fileIds }),
      signal: options?.abortSignal,
    });
    const body = await response.json<{ device_ids: string[] }>();
    return body.device_ids;
  }

  public async updateDevice(
    deviceId: string,
    updates: UpdateDeviceRequest,
    options?: Partial<Options>,
  ): Promise<Device> {
    if (!options?.resourceOwnerId) {
      throw Error("updateDevice requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/devices/id/${encodeDeviceId(deviceId)}`,
    );

    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      body: JSON.stringify(updates),
    });

    return await response.json<Device>();
  }

  /**
   * Deletes the device owned by `options.resourceOwnerId`.
   *
   * The device's configurations are deleted with it unless
   * `options.keepConfigurations` is set, which moves them into the
   * organization's assets instead (the service's `keep_configurations`
   * parameter). Keeping them is refused (403) while `release-asset-store` is
   * off for the org; deleting them is not.
   *
   * Resolves only on success; the route answers 204 with no body, so a failure
   * surfaces by rejecting rather than through a returned value.
   */
  public async deleteDevice(
    deviceId: string,
    options?: Partial<
      Pick<Options, "abortSignal" | "resourceOwnerId"> & {
        keepConfigurations: boolean;
      }
    >,
  ): Promise<void> {
    if (!options?.resourceOwnerId) {
      throw Error("deleteDevice requires an Org ID, none was provided");
    }

    const searchParams = new URLSearchParams();
    if (options.keepConfigurations) {
      searchParams.append("keep_configurations", "true");
    }
    const requestUrl = this.#httpClient.constructUrl(
      `v1/devices/id/${encodeDeviceId(deviceId)}`,
      searchParams,
    );

    const response = await this.#httpClient.delete(requestUrl, {
      idempotent: false,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      signal: options.abortSignal,
    });

    await response.throwIfError();
  }
}
